/**
 * @lodex-pro/web-portal/hooks/useOperatorWebhooks — the operator admin's webhook endpoints
 * (operator webhooks PR 1, migration 0239). OPERATOR_ADMIN only on the API
 * (MANAGE_OPERATOR_WEBHOOKS); the page never mounts these hooks for anyone else.
 *
 *   GET    /v1/operator-portal/webhooks              the endpoints, OP_WEBHOOKS on/off, its limit
 *   POST   /v1/operator-portal/webhooks              add; the secret comes back once
 *   PATCH  /v1/operator-portal/webhooks/:id          URL, description, events, pause/resume
 *   POST   /v1/operator-portal/webhooks/:id/rotate   a new secret; the old one signs for the overlap
 *   DELETE /v1/operator-portal/webhooks/:id          remove
 *
 * Every write invalidates the list. Response types are `import type` only (DEC-149 §9).
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type {
  CreateOperatorWebhookRequest,
  OperatorWebhookEndpointView,
  OperatorWebhookListResponse,
  OperatorWebhookSecretResponse,
  UpdateOperatorWebhookRequest,
} from '@lodex-pro/shared/validation/operator-webhooks';

export const PORTAL_WEBHOOKS_KEY: readonly string[] = ['operator-portal', 'webhooks'];
const BASE = '/v1/operator-portal/webhooks';

export function usePortalWebhooks(): ReturnType<typeof useApiQuery<OperatorWebhookListResponse>> {
  const opts = useApiOpts();
  return useApiQuery<OperatorWebhookListResponse>(opts, BASE, {
    queryKey: PORTAL_WEBHOOKS_KEY,
    staleTime: 10_000,
  });
}

function useInvalidate(): () => void {
  const qc = useQueryClient();
  return (): void => {
    void qc.invalidateQueries({ queryKey: PORTAL_WEBHOOKS_KEY });
  };
}

export function useAddPortalWebhook(): ReturnType<
  typeof useApiMutation<CreateOperatorWebhookRequest, OperatorWebhookSecretResponse>
> {
  const opts = useApiOpts();
  const invalidate = useInvalidate();
  return useApiMutation<CreateOperatorWebhookRequest, OperatorWebhookSecretResponse>(
    opts,
    BASE,
    'POST',
    { onSettled: invalidate },
  );
}

export function useUpdatePortalWebhook(
  id: string,
): ReturnType<
  typeof useApiMutation<UpdateOperatorWebhookRequest, { endpoint: OperatorWebhookEndpointView }>
> {
  const opts = useApiOpts();
  const invalidate = useInvalidate();
  return useApiMutation<UpdateOperatorWebhookRequest, { endpoint: OperatorWebhookEndpointView }>(
    opts,
    `${BASE}/${id}`,
    'PATCH',
    { onSettled: invalidate },
  );
}

export function useRotatePortalWebhook(
  id: string,
): ReturnType<typeof useApiMutation<{ overlapHours: number }, OperatorWebhookSecretResponse>> {
  const opts = useApiOpts();
  const invalidate = useInvalidate();
  return useApiMutation<{ overlapHours: number }, OperatorWebhookSecretResponse>(
    opts,
    `${BASE}/${id}/rotate`,
    'POST',
    { onSettled: invalidate },
  );
}

export function useRemovePortalWebhook(
  id: string,
): ReturnType<typeof useApiMutation<Record<string, never>, null>> {
  const opts = useApiOpts();
  const invalidate = useInvalidate();
  return useApiMutation<Record<string, never>, null>(opts, `${BASE}/${id}`, 'DELETE', {
    onSettled: invalidate,
  });
}

/** POST /webhooks/:id/test (webhooks PR 2): one signed TEST event now, and its outcome. */
export interface PortalWebhookTestResult {
  readonly delivered: boolean;
  readonly httpStatus: number | null;
  readonly error: string | null;
  readonly durationMs: number;
  readonly eventId: string;
}

export function useTestPortalWebhook(
  id: string,
): ReturnType<typeof useApiMutation<Record<string, never>, { result: PortalWebhookTestResult }>> {
  const opts = useApiOpts();
  return useApiMutation<Record<string, never>, { result: PortalWebhookTestResult }>(
    opts,
    `${BASE}/${id}/test`,
    'POST',
  );
}
